import {ButtonBar, DataGridActionBar} from "@dain/mui-components";
import {SubmissionDto, SubmissionFilterDto} from "../../../client/submission/SubmissionApiClient";
import {useEffect, useRef, useState} from "react";
import {Grid} from "@mui/material";
import {SearchButton} from "../../../common/button/CommonButtons";
import SubmissionGrid, {SubmissionColumn} from "../SubmissionGrid";
import {PageLoadSize} from "../../../common/pagination/PageableGrid";
import {SortingOrderType} from "../../../client/pagination/SortingFilterDto";
import {SubmissionFilterForm} from "./SubmissionFilterForm";
import {SearchPanelState, useSubmissionSearch} from "../useSubmissionSearch";

export const DEFAULT_PAGING_AND_SORTING_SUBMISSION_FILTER: SubmissionFilterDto = {
    paging: {
        maxPageSize: PageLoadSize.INITIAL_PAGE_SIZE,
        pageToken: null,
    },
    sorting: {
        sortingCriteria: [
            { property: "submittedAt", order: SortingOrderType.DESC },
            { property: "id", order: SortingOrderType.DESC }
        ],
    },
};

export interface SubmissionSearchPanelProps {
    actions?: React.ReactNode;
    isAdminOverview: boolean;
    anonymizePersonNames?: boolean;
    onRowDoubleClick?: (submissionId: number) => void;
    submissionsGridDefColumns?: SubmissionColumn[];
    onSelectedSubmission: (submission?: SubmissionDto) => void;
    onSubmissionDoubleClick?: (submissionId: number) => void;
}

export default function SubmissionSearchPanel(props: Readonly<SubmissionSearchPanelProps>) {
    const {actions, isAdminOverview, anonymizePersonNames, submissionsGridDefColumns, onSelectedSubmission, onSubmissionDoubleClick} = props;
    const isInitialized = useRef(false);

    const {
        submissionSearchPanelState,
        submissionFilter,
        onFilterChanged,
        submissionResults,
        displayedSubmissionsCount,
        filterSearch,
        fetchSubmissions,
        reloadDefaultSubmissionGrid,
    } = useSubmissionSearch(DEFAULT_PAGING_AND_SORTING_SUBMISSION_FILTER, anonymizePersonNames);

    const [selectedSubmission, setSelectedSubmission] = useState<SubmissionDto>();

    function onSelectionChange(submission?: SubmissionDto) {
        setSelectedSubmission(submission);
        onSelectedSubmission(submission);
    }

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            reloadDefaultSubmissionGrid();
        }
    }, [reloadDefaultSubmissionGrid]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <SubmissionFilterForm
                        filter={submissionFilter}
                        isAdminOverview={isAdminOverview}
                        onChange={onFilterChanged}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ButtonBar>
                        <SearchButton onClick={filterSearch} />
                    </ButtonBar>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        {actions && (
                            <Grid item xs={12}>
                                <DataGridActionBar actions={actions} />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            {submissionSearchPanelState !== SearchPanelState.INITIALIZED && (
                                <SubmissionGrid
                                    rowsData={submissionResults.items}
                                    totalCount={displayedSubmissionsCount}
                                    nextPageToken={submissionResults.nextPageToken}
                                    submissionsGridDefColumns={submissionsGridDefColumns}
                                    submissionFilter={submissionFilter}
                                    fetchSubmissions={fetchSubmissions}
                                    selectedSubmissionId={selectedSubmission?.id}
                                    onSelectSubmission={onSelectionChange}
                                    onSubmissionDoubleClick={onSubmissionDoubleClick}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
